import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-time",
  title: "Bible verses about time: 10 passages on using it well",
  h1: "Bible verses about time",
  description:
    "Ten passages on time, seasons and how short a life is — with notes on what each one asks, for anyone who ended the week unable to say where it went.",
  targets: [
    "bible verses about time",
    "scripture on using time wisely",
    "what does the bible say about wasting time",
  ],
  published: "2026-08-26",
  standfirst:
    "Scripture is unsentimental about how much time there is. What it asks for is not efficiency — it is attention to which season you are actually in.",
  takeaways: [
    "Psalm 90:12 asks to be taught to count days, which is a request for perspective rather than for productivity — the outcome named is a heart of wisdom.",
    "Ephesians 5:16, redeeming the time, is a market image: buying back something that was going to be lost anyway.",
    "Ecclesiastes 3 refuses to rank the seasons — there is a time to plant and a time to uproot, and neither is the better one.",
    "James 4:14 calls a life a vapour, and the conclusion drawn is humility about plans rather than urgency about schedules.",
  ],
  intro: [
    {
      type: "p",
      text: "The reason people search for verses about time is rarely philosophical. It is Sunday evening, the week has gone, and the honest account of where it went is unsatisfying — a great deal of it in pieces, none of them memorable. The modern version of the problem is not idleness; almost nobody is idle. It is fragmentation, which produces the same result while feeling like activity.",
    },
    {
      type: "p",
      text: "Scripture's treatment of time is not a productivity system, and the passages below do not add up to one. What they ask for repeatedly is a sense of scale — how short it is, which season this is, what today is actually for. That is a different question from how much was accomplished, and it is the one that makes a fragmented week visible for what it was.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "So teach us to count our days, that we may gain a heart of wisdom.",
        ref: "Psalm 90:12",
      },
      note: "A prayer to be taught something rather than a resolution to do it, and the outcome asked for is wisdom, not output. The psalm around it is a sustained meditation on how brief a life is; the request follows from that, and it is asking for perspective at the scale of decades, not for a better morning routine.",
    },
    {
      verse: {
        text: "Therefore watch carefully how you walk, not as unwise, but as wise, redeeming the time, because the days are evil.",
        ref: "Ephesians 5:15–16",
      },
      note: "Redeeming is a commercial word — buying back, at cost, something otherwise forfeit. That framing assumes time is being lost by default rather than spent by choice, which is an unusually accurate description of how a day disappears. The instruction is to buy some of it back, not to account for all of it.",
    },
    {
      verse: {
        text: "For everything there is a season, and a time for every purpose under heaven.",
        ref: "Ecclesiastes 3:1",
      },
      note: "The list that follows refuses to rank its pairs. A time to plant and a time to uproot; the chapter does not say which is better, only that both are real and that they arrive in turn. Most anxiety about time is really anxiety about being in the wrong season, and this passage declines to confirm it.",
    },
    {
      verse: {
        text: "Yet you don't know what your life will be like tomorrow. For what is your life? For you are a vapor that appears for a little time, and then vanishes away.",
        ref: "James 4:14",
      },
      note: "The conclusion James draws is not hurry but humility — say instead, if the Lord wills, we will do this. The brevity is an argument against confident planning rather than an argument for filling every hour, which is the opposite of how the verse is usually deployed.",
    },
    {
      verse: {
        text: "Yahweh, show me my end, what is the measure of my days.",
        ref: "Psalm 39:4",
      },
      note: "Blunt, and slightly uncomfortable. The psalmist is asking to be shown the limit, and the psalm is candid that the answer is not consoling. It belongs in this list precisely because it does not tidy the subject up.",
    },
    {
      verse: {
        text: "This is the day that Yahweh has made. We will rejoice and be glad in it.",
        ref: "Psalm 118:24",
      },
      note: "Worn smooth by repetition, and worth recovering. The claim is about this particular day — the ordinary one currently in progress — rather than about days in general. Attention to the present is the practice, and it is the exact capacity that fragmentation removes.",
    },
    {
      verse: {
        text: "It is vain for you to rise up early, to stay up late, eating the bread of toil; for he gives sleep to his loved ones.",
        ref: "Psalm 127:2",
      },
      note: "The clearest verse in scripture on overwork, and it names the hours: early and late. The judgment is not moral but practical — vain, meaning it does not produce what it promises. The modern version of the extra hour is rarely work anyway; it is a screen at both ends of the day.",
    },
    {
      verse: {
        text: "Don't boast about tomorrow; for you don't know what a day may bring.",
        ref: "Proverbs 27:1",
      },
      note: "The shortest correction to the habit of spending time that has not arrived yet. Most plans about attention are made about a future week that is assumed to be quieter than this one, and Proverbs declines to grant the assumption — the only week you can actually decide about is the one already running.",
    },
    {
      verse: {
        text: "Behold, now is the acceptable time. Behold, now is the day of salvation.",
        ref: "2 Corinthians 6:2",
      },
      note: "About decision rather than scheduling. Its relevance to a list like this is the word now: a great many things get postponed into a version of next month that never has fewer demands in it than this one, and the postponement is usually the decision.",
    },
    {
      verse: {
        text: "Teach me your way, Yahweh. I will walk in your truth. Make my heart undivided to fear your name.",
        ref: "Psalm 86:11",
      },
      note: "An undivided heart is the closest thing scripture has to the modern problem of attention. The request is for singleness — one direction rather than several partial ones — and it is asked for rather than resolved upon, which is the right way round for anyone who has tried and failed to concentrate by deciding to.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "counting-days",
      text: "What counting your days looks like in practice",
    },
    {
      type: "p",
      text: "Not a spreadsheet. Psalm 90 is asking for a sense of scale, and the practical version is closer to a weekly review than to a time log: one page, once a week, answering what this week was actually for. Most people who do it for a month discover that the missing hours are not in any of the things they were worried about — they are in fragments, distributed so evenly that no single instance is worth mentioning.",
    },
    {
      type: "p",
      text: "The second practice is the one Ephesians implies: buy back a block rather than trimming everywhere. One protected hour is worth more than fifteen minutes saved in four places, because the fifteen-minute pieces cannot hold anything that takes concentration. {The-piece-on-screen-time-limits} explains why the tools most people reach for fail at exactly this, and what replaces them.",
      links: {
        "The-piece-on-screen-time-limits": "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "h2",
      id: "a-prayer-about-time",
      text: "A prayer for the end of a week",
    },
    {
      type: "p",
      text: "Lord, teach me to count my days. Not to fill them — to know what they are for. Buy back the hours I lost without deciding to lose them, give me an undivided heart for tomorrow, and let me be content with the season I am actually in rather than the one I keep planning. Amen.",
    },
  ],
  faq: [
    {
      q: "Does the Bible say wasting time is a sin?",
      a: "It does not use that phrase. Ephesians 5:16 treats time as something to be bought back, Proverbs is severe about sloth, and Ecclesiastes insists there is a season for doing nothing in particular. The picture is about attention and season; read as a ledger of hours it produces anxiety rather than change.",
    },
    {
      q: "What does “redeeming the time” actually mean?",
      a: "The verb is a market one: buying something back that would otherwise be forfeited. It assumes loss is the default state and that recovering some of it costs something. It is not a call to optimise every hour, which is a modern reading of a first-century phrase.",
    },
    {
      q: "Which translation is used here?",
      a: "The World English Bible, which is in the public domain. It prints Yahweh where many translations have LORD.",
    },
  ],
  related: [
    "screen-time-limits-dont-work",
    "digital-sabbath",
    "bible-in-a-year-on-your-phone",
  ],
  relatedTopics: ["bible-verses-about-rest", "bible-verses-about-comparison"],
};
